import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { authConfigsSelector } from 'slices/authConfig.slice';
import { exportedUserColumns } from '@timeedit/types';
import { EditableTable, TEInput } from '@timeedit/ui-components';

export type UserPreview = Partial<
  Record<(typeof exportedUserColumns)[keyof typeof exportedUserColumns], string>
>;

type ExportedUserColumn =
  (typeof exportedUserColumns)[keyof typeof exportedUserColumns];

// Wide free-text columns whose long, comma-joined values would otherwise
// inflate the table past the modal width. Capping their width + ellipsis,
// combined with the table's horizontal scroll, keeps the modal contained.
const wideColumnValues = new Set<string>([
  exportedUserColumns.organizationsAndRoles,
  exportedUserColumns.scopes,
  exportedUserColumns.appPermissions,
  exportedUserColumns.id,
  exportedUserColumns.email,
  exportedUserColumns.contactEmail,
  exportedUserColumns.userObjects,
]);

type Props = {
  data: UserPreview[];
  columns: string[];
  authConfigId: string;
  setAuthConfigId: (value: string) => void;
};

export const UserPreview = ({
  data,
  columns,
  authConfigId,
  setAuthConfigId,
}: Props) => {
  const authConfigs = useSelector(authConfigsSelector);
  const userPreviewColumns = useMemo(() => {
    const allowedColumns = new Set<ExportedUserColumn>(
      Object.values(exportedUserColumns)
    );
    const isExportedColumn = (value: string): value is ExportedUserColumn =>
      allowedColumns.has(value as ExportedUserColumn);

    return columns.filter(isExportedColumn).map((value) => ({
      title: value,
      key: value,
      dataIndex: value,
      ...(wideColumnValues.has(value)
        ? { width: 280, ellipsis: { showTitle: true } as const }
        : {}),
    }));
  }, [columns]);

  return (
    <div>
      <div style={{ width: '50%', margin: '50px 0px 20px' }}>
        <TEInput.Select
          label="Auth Config"
          value={authConfigId}
          onChange={setAuthConfigId}
          options={authConfigs.map((authConfig) => ({
            label: authConfig.name,
            value: authConfig.id,
          }))}
          placeholder="Select"
          tooltip="Auth Config"
        />
      </div>
      <EditableTable
        pagination={{ size: 'small' }}
        dataSource={data}
        columns={userPreviewColumns}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};
